import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, CalendarRange } from "lucide-react";
import { useApp, formatMoney, useCanEdit } from "@/lib/app-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StaffDialog } from "@/components/StaffDialog";
import { SearchBox, matches } from "@/components/SearchBox";

export default function StaffPage() {
  const { api } = useApp();
  const canEdit = useCanEdit();
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setStaff(await api("get", "/staff")), [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await api("put", `/staff/${form.id}`, form);
      else await api("post", "/staff", form);
      toast.success("Staff saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Please check the name and salary");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the staff list? Past months stay as they are.`)) return;
    await api("delete", `/staff/${s.id}`);
    toast.success("Staff removed");
    load();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="staff-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Staff list</h1>
          <p className="text-sm text-slate-600 mt-1">Saved once. Each new month starts with these salaries.</p>
        </div>
        {canEdit && <Button onClick={() => setEditing({})} className="bg-teal-600 hover:bg-teal-700" data-testid="add-staff-button"><Plus size={16} />Add staff</Button>}
      </div>
      {staff.length > 0 && <SearchBox value={q} onChange={setQ} testId="staff-search" />}
      {staff.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="staff-empty">No staff yet. Add your first staff member.</div>
      ) : staff.filter((s) => matches(q, s.name, s.agent_name)).length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center text-slate-500" data-testid="staff-no-match">No staff match “{q}”.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {staff.filter((s) => matches(q, s.name, s.agent_name)).map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`staff-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{s.name}</div>
                  <div className="text-xs text-slate-500">Agent: {s.agent_name || "—"} · Joined {s.joining_date || "—"}</div>
                </div>
                {!s.active && <Badge variant="secondary">Inactive</Badge>}
              </div>
              <div className="flex items-end justify-between mt-4">
                <div className="font-mono text-xl font-bold">{formatMoney(s.base_salary, s.currency || "INR")}</div>
                <div className="flex gap-1">
                  <Button asChild size="icon" variant="ghost" title="Yearly summary"><Link to={`/staff/${s.id}`} data-testid={`year-summary-${s.id}`}><CalendarRange size={16} /></Link></Button>
                  {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(s)} data-testid={`edit-staff-${s.id}`}><Pencil size={16} /></Button>}
                  {canEdit && <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}><Trash2 size={16} /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <StaffDialog staff={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
